import React, { Component } from 'react';
import { AlertTriangle, RotateCcw, Bug, ArrowRight } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { BaseErrorBoundary, ErrorFallbackProps } from '../../components/shared/BaseErrorBoundary';
import { DjayNarrator } from '../../mascot';
/**
 * QuestionErrorBoundary — Catches rendering errors in question renderers.
 *
 * Prevents renderer crashes from breaking the entire practice session.
 * Shows a friendly fallback UI with skip/reset functionality.
 * Includes dev-only debug panel with question ID, error, and stack trace.
 * Now uses BaseErrorBoundary for shared lifecycle logic (s106 P2-18).
 *
 * Co-located in practice/ domain (CL-1 s63).
 */
// ── Types ────────────────────────────────────────────────────
interface QuestionErrorBoundaryProps {
  children: React.ReactNode;
  questionId: string;
  onReset: () => void;
  onSkipQuestion?: () => void;
}
// ── Component ────────────────────────────────────────────────
export class QuestionErrorBoundary extends Component<QuestionErrorBoundaryProps> {
  renderFallback = ({
    error,
    errorInfo,
    onReset
  }: ErrorFallbackProps): React.ReactNode => {
    return <div role="alert" aria-live="assertive" className="w-full max-w-2xl mx-auto px-4 py-8" data-id="element-11261" data-mp="21s9dmbtgq851">
        <Card className="p-8 border-2 border-amber-200 bg-amber-50/50 shadow-xl shadow-amber-100" data-id="element-11262" data-mp="1wsdasfwi4mr2">
          {/* Header with Djay */}
          <div className="flex items-start gap-4 mb-6" data-id="element-11263" data-mp="1mvs6jwsb3foa">
            <DjayNarrator emotion="confused" size="lg" className="flex-shrink-0" data-id="element-11264" data-mp="1myecjrzjtn4e" />
            <div className="flex-1" data-id="element-11265" data-mp="1dvvedl6pjyoh">
              <div className="flex items-center gap-2 mb-2" data-id="element-11266" data-mp="1dwwwuaq1zng8">
                <AlertTriangle className="w-5 h-5 text-amber-600" data-id="element-11267" data-mp="3d9973arjqt0n" />
                <h3 className="text-xl font-bold text-slate-900" data-id="element-11268" data-mp="3d6mwt3d9tzeq">
                  Deze vraag kon niet worden geladen
                </h3>
              </div>
              <p className="text-slate-700 leading-relaxed" data-id="element-11269" data-mp="3rd6popt2it5x">
                Er ging iets mis bij het tonen van deze vraag. Je kunt de vraag
                overslaan of opnieuw proberen.
              </p>
            </div>
          </div>

          {/* Dev Debug Panel */}
          {import.meta.env?.DEV && error && <div className="mb-6 p-4 bg-rose-50 rounded-lg border border-rose-200" data-id="element-11270" data-mp="3ncwbipv4gwfz">
              <div className="flex items-center gap-2 mb-2" data-id="element-11271" data-mp="ey5v99k06zri">
                <Bug className="w-4 h-4 text-rose-600" data-id="element-11272" data-mp="iyy6g8cfreqf" />
                <p className="text-xs font-bold text-rose-800" data-id="element-11273" data-mp="ixmzy0rlvsvm">
                  Debug Info (dev only)
                </p>
              </div>
              <div className="space-y-2 text-xs font-mono text-rose-700" data-id="element-11274" data-mp="9x8f12ik0fsn">
                <div data-id="element-11275" data-mp="9zkrx19gfzyu">
                  <span className="font-bold" data-id="element-11276" data-mp="2pocsvdw6ldvu">Question ID:</span>{' '}
                  {this.props.questionId}
                </div>
                <div data-id="element-11277" data-mp="2uos2pvh98392">
                  <span className="font-bold" data-id="element-11278" data-mp="2ulw5689bjgl5">Error:</span> {error.message}
                </div>
                {error.stack && <div className="mt-2 p-2 bg-rose-100 rounded overflow-x-auto" data-id="element-11279" data-mp="2i5n08y1l9xj8">
                    <pre className="text-[10px] whitespace-pre-wrap break-all" data-id="element-11280" data-mp="2i7pmr83j98j7">
                      {error.stack}
                    </pre>
                  </div>}
                {errorInfo?.componentStack && <div className="mt-2 p-2 bg-rose-100 rounded overflow-x-auto" data-id="element-11281" data-mp="23fyvtny42ira">
                    <div className="font-bold mb-1" data-id="element-11282" data-mp="1zf5an2c1zi23">Component Stack:</div>
                    <pre className="text-[10px] whitespace-pre-wrap" data-id="element-11283" data-mp="1zcsmg7fpcbgl">
                      {errorInfo.componentStack}
                    </pre>
                  </div>}
              </div>
            </div>}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3" data-id="element-11284" data-mp="1koxs6mm1pz1e">
            {this.props.onSkipQuestion && <Button onClick={() => {
            onReset();
            this.props.onSkipQuestion?.();
          }} variant="primary" size="xl" fullWidth className="shadow-lg shadow-indigo-200" data-id="element-11285" data-mp="1fq4otn0h3oey">
                <ArrowRight className="w-5 h-5 mr-2" data-id="element-11286" data-mp="23a83mbvxawhz" />
                Sla deze vraag over
              </Button>}
            <Button onClick={onReset} variant="secondary" size="xl" fullWidth data-id="element-11287" data-mp="284cakv56v4j2">
              <RotateCcw className="w-5 h-5 mr-2" data-id="element-11288" data-mp="2c3uupop6rbku" />
              Probeer opnieuw
            </Button>
          </div>
        </Card>
      </div>;
  };
  render(): React.ReactNode {
    return <BaseErrorBoundary logPrefix="Question Renderer" logContext={{
      questionId: this.props.questionId
    }} resetKey={this.props.questionId} fallback={this.renderFallback} onReset={this.props.onReset} respectStrictMode data-id="element-11289" data-mp="2m2twqofg48yv">
        {this.props.children}
      </BaseErrorBoundary>;
  }
}